/**
 * OperatorPage
 *
 * Persistent page for the operator UI. Two-column layout:
 *
 *   [Narrow Chat Panel] | [Canvas (dominant)]
 *
 * The canvas is primary — it shows the operator's task-dashboard surface
 * with DelegatedTaskCard components. The chat thread is secondary (~350px,
 * collapsible). Conversation switching lives in the global operator sidebar
 * (ChatNavItem), so this page no longer renders an inline conversation list.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMatch } from 'react-router-dom';
import type { ThreadMessageLike } from '@assistant-ui/react';
import { Network, MessageSquare, Pin } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ConnectionIndicator } from '../../components/ui';
import OperatorChatPanel from './OperatorChatPanel';
import { ConversationDetailsPopover } from './ConversationDetailsPopover';
import OperatorLoadingSkeleton from './OperatorLoadingSkeleton';
import { CollapsedPanelBar, type CollapsedPanel } from '../../components/ui';
import { UIEventProvider, useUIEvent } from '../../contexts/UIEventContext';
import { ThoughtsProvider } from '../../contexts/ThoughtsContext';
import { CanvasProvider } from '../../contexts/CanvasContext';
import { AssistantThreadActionsProvider } from '../../contexts/AssistantThreadActionsContext';
import { CanvasPanel } from '../../components/canvas/CanvasPanel';
import { CanvasUrlSync } from '../../components/canvas/CanvasUrlSync';
import { CanvasHotkeys } from '../../components/canvas/CanvasHotkeys';
import { useAgentRuntime } from '../../hooks/useAgentRuntime';
import { useAgentRunEvents } from '../../hooks/useAgentRunEvents';
import { useConversationRestore, type CanvasHint } from '../../hooks/useConversationRestore';
import { useResumeInFlightRun } from '../../hooks/useResumeInFlightRun';
import {
  useConversation,
  useConversationSpaceId,
  useUpdateConversation,
  useTogglePinConversation,
} from '../../hooks/useConversations';
import { InlineEdit } from '../../components/ui/InlineEdit';
import { useMeAgent as useAgent } from '../../hooks/useAgents';
import { useToolNameMap, ToolNameProvider } from '../../hooks/useTools';
import { useDispatchAction } from '../../hooks/useDispatchAction';
import { useConversationNamedEvent } from '../../hooks/useConversationNamedEvent';
import { usePlatformEvents } from '../../contexts/PlatformEventContext';
import { getOrCreateConnection, closeConnection } from '../../utils/direct-run-connection';
import { useAuth } from '../../store/auth-context';

const CHAT_COLLAPSED_KEY = 'operator-chat-collapsed';

interface OperatorPageProps {
  visible?: boolean;
}

export default function OperatorPage({ visible = true }: OperatorPageProps) {
  const match = useMatch('/chat/:conversationId');
  const rawConversationId = match?.params.conversationId ?? null;
  // /chat/new is the agent picker page, not a real conversation id.
  const urlConversationId =
    rawConversationId === 'new' ? null : rawConversationId;
  const { user, updatePreferences } = useAuth();

  // ── Critical path: warm the direct run socket the instant we know the URL
  // conversation id, in parallel with (and unblocked by) all other boot data.
  // Presign is un-scoped, so this needs no `me` / spaces / list / agent —
  // just the id from the URL. The micro-VM is warming while the rest loads.
  const prewarmedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlConversationId) return;
    if (prewarmedRef.current && prewarmedRef.current !== urlConversationId) {
      closeConnection(prewarmedRef.current);
    }
    prewarmedRef.current = urlConversationId;
    getOrCreateConnection(urlConversationId, {}).prewarm();
  }, [urlConversationId]);

  useConversationNamedEvent();

  const [conversationKey, setConversationKey] = useState(0);
  const [prevUrlConversationId, setPrevUrlConversationId] = useState<string | null>(urlConversationId);

  // Sync URL → state
  if (urlConversationId !== prevUrlConversationId) {
    setPrevUrlConversationId(urlConversationId);
    setConversationKey((k) => k + 1);
  }

  // Persist the active chat so the operator surface can resume it next visit.
  useEffect(() => {
    if (!urlConversationId) return;
    if (user?.preferences?.lastTopicConversationId === urlConversationId) return;
    updatePreferences({ lastTopicConversationId: urlConversationId }).catch(() => {
      // Best-effort — losing the preference write isn't worth interrupting the user
    });
  }, [urlConversationId, user?.preferences?.lastTopicConversationId, updatePreferences]);

  const conversationSpaceId = useConversationSpaceId(urlConversationId ?? undefined);
  return (
    <UIEventProvider spaceId={conversationSpaceId}>
      <ThoughtsProvider>
        {urlConversationId && (
          <OperatorConversationLoader
            key={conversationKey}
            conversationId={urlConversationId}
            visible={visible}
          />
        )}

        {visible && !urlConversationId && (
          <div className="flex h-screen items-center justify-center">
            <div className="text-center">
              <Network className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium text-foreground">
                Operator
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Select a chat from the sidebar or start a new conversation.
              </p>
            </div>
          </div>
        )}
      </ThoughtsProvider>
    </UIEventProvider>
  );
}

// ─── Conversation loader (tier 2) ──────────────────────────────────────────

interface ConversationLoaderProps {
  conversationId: string;
  visible: boolean;
}

function OperatorConversationLoader({
  conversationId,
  visible,
}: ConversationLoaderProps) {
  const { dispatchUIEvent } = useUIEvent();
  const { user } = useAuth();
  const conversationSpaceId = useConversationSpaceId(conversationId);
  const { data: conversationMeta } = useConversation(conversationId);
  const agentId = conversationMeta?.agentId;

  const { messages: initialMessages, canvasHints, defaultCanvas, loading, inFlightRun } =
    useConversationRestore({
      conversationId,
      spaceId: conversationSpaceId,
      dispatchUIEvent,
      fetchRunDetails: false,
      fetchDelegatedTasks: true,
    });

  const ready = !loading && !!agentId;
  const [skeletonMounted, setSkeletonMounted] = useState(true);
  const [skeletonVisible, setSkeletonVisible] = useState(true);

  useEffect(() => {
    if (!ready) return;
    const r = requestAnimationFrame(() => setSkeletonVisible(false));
    const t = setTimeout(() => setSkeletonMounted(false), 600);
    return () => {
      cancelAnimationFrame(r);
      clearTimeout(t);
    };
  }, [ready]);

  if (!visible) return null;

  return (
    <div className="relative h-screen">
      {ready && (
        <div
          className={cn(
            'absolute inset-0',
            // Entry fade only when the tab can actually run it: a keyframe
            // animation started inside a hidden tab freezes at its first
            // frame (opacity 0) in Safari, leaving the pane invisible until
            // reload. Mounting while hidden skips the fade entirely.
            (typeof document === 'undefined' || document.visibilityState === 'visible') &&
              'animate-in fade-in duration-500',
          )}
        >
          <OperatorThread
            conversationId={conversationId}
            agentId={agentId!}
            visible={visible}
            initialMessages={initialMessages}
            canvasHints={canvasHints}
            defaultCanvas={defaultCanvas}
            inFlightRun={inFlightRun}
          />
        </div>
      )}
      {skeletonMounted && (
        <div
          className={cn(
            'absolute inset-0 transition-opacity duration-500 ease-in-out',
            skeletonVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <OperatorLoadingSkeleton
            chatWidth={user?.preferences?.operatorChatWidth}
            chatTitle={conversationMeta?.name || 'New Conversation'}
          />
        </div>
      )}
    </div>
  );
}

// ─── Thread holder (tier 3) ────────────────────────────────────────────────

interface OperatorThreadProps {
  conversationId: string;
  agentId: string;
  visible: boolean;
  initialMessages?: readonly ThreadMessageLike[];
  canvasHints?: CanvasHint[];
  defaultCanvas?: import('../../hooks/useConversationRestore').ConversationRestoreResult['defaultCanvas'];
  inFlightRun?: import('../../hooks/useConversationRestore').ConversationRestoreResult['inFlightRun'];
}

function OperatorThread({
  conversationId,
  agentId,
  visible,
  initialMessages,
  canvasHints,
  defaultCanvas,
  inFlightRun,
}: OperatorThreadProps) {
  const { dispatchUIEvent } = useUIEvent();
  const { user, updatePreferences } = useAuth();
  const handleConversationChange = useCallback(() => {
    // Conversation ID is already known — no preference tracking needed
  }, []);

  const conversationSpaceId = useConversationSpaceId(conversationId);
  const { runtime, conversationIdRef, activeRunIdStore, getToolName, consumeAttachmentsRef } = useAgentRuntime({
    agentId,
    spaceId: conversationSpaceId,
    initialConversationId: conversationId,
    onConversationChange: handleConversationChange,
    initialMessages,
    dispatchUIEvent,
  });
  useAgentRunEvents(runtime, activeRunIdStore, conversationIdRef, dispatchUIEvent, getToolName, visible, conversationId);
  // Reloaded/returned mid-run: re-attach the still-executing run so its live
  // trace resumes instead of looking lost.
  useResumeInFlightRun({ runtime, inFlightRun, enabled: visible });

  const { data: agent } = useAgent(agentId);
  const { data: conversationMeta } = useConversation(conversationId);
  const updateConversation = useUpdateConversation();
  const togglePinConversation = useTogglePinConversation();
  const isPinned = !!conversationMeta?.pinned;
  const toolNameMap = useToolNameMap(agent?.tools);

  const handleAction = useDispatchAction(agentId, conversationSpaceId, conversationIdRef);
  const { isConnected, diagnostics } = usePlatformEvents();

  const submitUserMessage = useCallback(
    (text: string) => {
      runtime.thread.append({
        role: 'user',
        content: [{ type: 'text', text }],
      });
    },
    [runtime],
  );
  const threadActions = useMemo(() => ({ submitUserMessage }), [submitUserMessage]);

  // Chat panel collapse state
  const [chatCollapsed, setChatCollapsed] = useState(
    () => localStorage.getItem(CHAT_COLLAPSED_KEY) === 'true',
  );
  const toggleChat = useCallback(() => {
    setChatCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(CHAT_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const handleChatWidthCommit = useCallback(
    (width: number) => {
      updatePreferences({ operatorChatWidth: width }).catch(() => {
        // Best-effort — width persistence isn't critical
      });
    },
    [updatePreferences],
  );

  // Build collapsed panel chips
  const collapsedPanels = useMemo<CollapsedPanel[]>(() => {
    const panels: CollapsedPanel[] = [];
    if (chatCollapsed) {
      panels.push({
        id: 'chat',
        label: 'Chat',
        icon: MessageSquare,
        onExpand: toggleChat,
      });
    }
    return panels;
  }, [chatCollapsed, toggleChat]);

  if (!visible) return null;

  return (
    <CanvasProvider
      conversationId={conversationIdRef.current ?? conversationId}
      spaceId={conversationSpaceId}
      canvasHints={canvasHints}
      initialCanvas={defaultCanvas}
      autoScrollOnUpdate={false}
    >
      <AssistantThreadActionsProvider value={threadActions}>
        <CanvasUrlSync />
        <CanvasHotkeys />
        <ToolNameProvider value={toolNameMap}>
          <div className="flex h-screen flex-col">
            {/* Slim indicator bar for collapsed panels */}
            <CollapsedPanelBar panels={collapsedPanels} />

            {/* Two-column layout */}
            <div className="flex min-h-0 flex-1">
              {/* Narrow, collapsible chat thread */}
              <OperatorChatPanel
                runtime={runtime}
                agentId={agentId}
                consumeAttachmentsRef={consumeAttachmentsRef}
                collapsed={chatCollapsed}
                onToggle={toggleChat}
                initialWidth={user?.preferences?.operatorChatWidth}
                onWidthCommit={handleChatWidthCommit}
                header={
                  <h2 className="block min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
                    <InlineEdit
                      value={conversationMeta?.name || ''}
                      placeholder="New Conversation"
                      onSave={async (name) => {
                        await updateConversation.mutateAsync({
                          conversationId,
                          input: { name: name || undefined },
                        });
                      }}
                    />
                  </h2>
                }
                headerActions={
                  <>
                    {conversationMeta && (
                      <button
                        type="button"
                        onClick={() =>
                          togglePinConversation.mutate({
                            conversationId,
                            pinned: !isPinned,
                          })
                        }
                        className={cn(
                          'rounded-md p-1 hover:bg-muted',
                          isPinned
                            ? 'text-primary'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                        title={isPinned ? 'Unpin conversation' : 'Pin conversation'}
                        aria-label={isPinned ? 'Unpin conversation' : 'Pin conversation'}
                        aria-pressed={isPinned}
                      >
                        <Pin className={cn('h-4 w-4', isPinned && 'fill-current')} />
                      </button>
                    )}
                    <ConversationDetailsPopover
                      conversation={conversationMeta}
                      agent={agent}
                    />
                  </>
                }
              />

              {/* Canvas — takes all remaining space */}
              <div className="flex min-w-0 flex-1 flex-col">
                <CanvasPanel
                  onAction={handleAction}
                  headerActions={
                    <ConnectionIndicator isConnected={isConnected} diagnostics={diagnostics} />
                  }
                />
              </div>
            </div>
          </div>
        </ToolNameProvider>
      </AssistantThreadActionsProvider>
    </CanvasProvider>
  );
}
